import { ReactElement, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { duplicateTimetable } from 'src/api/duplicateTimetable';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { useTimetables } from 'src/hooks/useTimetables';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { DuplicateNameTrayContent, ICloseDuplicateTrayOptions } from '../DuplicateNameTray';

const EXISTING_NAMES_STALE_TIME = 5 * 60 * 1000;

export interface DuplicateTimetableTrayContentProps {
  timetableId: string;
  sourceName?: string;
  onClose: (options?: ICloseDuplicateTrayOptions) => void;
  openId: number;
}

export const DuplicateTimetableTrayContent = ({
  timetableId,
  sourceName,
  onClose,
  openId,
}: DuplicateTimetableTrayContentProps): ReactElement => {
  const queryClient = useQueryClient();
  const { trackEvent } = useUserTracking();
  const { data: timetablesData } = useTimetables(15, undefined, undefined, { staleTime: EXISTING_NAMES_STALE_TIME });
  const existingTimetableNames = useMemo(
    () => timetablesData?.pages.flatMap((page) => page.data.map((timetable) => timetable.name)) ?? [],
    [timetablesData],
  );

  const handleDuplicate = async (name: string) => {
    await duplicateTimetable(timetableId, { name });
    await queryClient.invalidateQueries({ queryKey: [QueryKeys.timetables] });
    trackEvent('timetable_duplicated', { timetableId });
  };

  return (
    <DuplicateNameTrayContent
      entityLabel="timetable"
      nameLabel="Timetable Name (internal only)"
      sourceName={sourceName}
      existingNames={existingTimetableNames}
      errorTag="timetable_duplicate"
      openId={openId}
      onDuplicate={handleDuplicate}
      onClose={onClose}
    />
  );
};

export default DuplicateTimetableTrayContent;
